import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Input,
  InputNumber,
  notification,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tabs,
  Typography,
} from 'antd';
import { ArrowLeftOutlined, DeleteOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';
import lib from '../../assets/utils/lib';
import CheckinTypeTypeahead from './CheckinTypeTypeahead';
import {
  buildTariffLookupParams,
  categoryEvidenceRuleFromApi,
  categoryOptionsFromHydrated,
  categoryOptionsFromLookupList,
  categoryPolicyFromTariffLinks,
  EVIDENCE_MODES,
  entitlementFromBundle,
  facilityOptionsFromBundle,
  facilityTypeOptionsFromBundle,
  facilityTypeOptionsFromLookupList,
  itemRuleFromApi,
  itemRuleValue,
  tariffLinkFromApi,
  tariffServiceGroupSummary,
  workflowFromBundle,
} from './checkinTypeNormalize';
import LinkedPlansPanel from './LinkedPlansPanel';
import {
  CHECKIN_MESSAGE_PLACEHOLDERS,
  EVIDENCE_BUNDLE_PRESET,
  EVIDENCE_HEADER_HINT,
  EVIDENCE_HEADER_LABEL,
  EVIDENCE_LINE_HINT,
  EVIDENCE_LINE_LABEL,
  EVIDENCE_MODE_HELP,
  FieldHint,
  TabGoal,
} from './CheckinTypeFormHelp';
import {
  SetupWizardNav,
  PolicyModePicker,
  EntitlementModePicker,
  EvidenceModePicker,
  LiveSummary,
  NextStepHint,
  describePolicyMode,
  WIZARD_STEPS,
} from './CheckinTypeFormControls';

const { Title, Text } = Typography;

const getServerError = (e, fallback) =>
  (e?.response?.data?.message != null && e.response.data.message !== '')
    ? e.response.data.message
    : (e?.message || fallback);

const emptyTariffLink = () => ({
  key: `tl-${Date.now()}-${Math.random()}`,
  tariff_kind: 'standard',
  standard_tariff_id: null,
  standard_tariff_label: null,
  facility_tariff_id: null,
  facility_tariff_label: null,
  link_facility_id: null,
  link_facility_label: null,
  category_id: null,
});

const emptyItemRule = () => ({
  key: `ir-${Date.now()}-${Math.random()}`,
  label: null,
  standardized_item_id: null,
  item_id: null,
  tariff_item_id: null,
  facility_tariff_item_id: null,
  evidence_mode: 'not_needed',
});

const emptyCategoryEvidenceRule = () => ({
  key: `cer-${Date.now()}-${Math.random()}`,
  category_id: null,
  category_name: null,
  evidence_mode: 'not_needed',
});

const mergeSelectOptions = (existing, incoming) => {
  const map = new Map((existing || []).map((o) => [o.value, o]));
  (incoming || []).forEach((o) => {
    if (o?.value != null) map.set(o.value, o);
  });
  return [...map.values()];
};

/**
 * Props let this page be hosted inside the Plan Management workspace (id passed
 * in, back/create handled by the host) instead of its own route. With no props
 * it behaves exactly as the standalone /plans/encounter-types/:id route.
 */
const AdminCheckinTypeDetail = ({ checkinTypeId: checkinTypeIdProp, onBack, onCreated } = {}) => {
  const routeParams = useParams();
  const checkinTypeId = checkinTypeIdProp ?? routeParams.checkinTypeId;
  const navigate = useNavigate();
  const isNew = checkinTypeId === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('general');

  const [generalForm] = Form.useForm();
  const [entitlementForm] = Form.useForm();
  const [workflowForm] = Form.useForm();

  const [otherTypeOptions, setOtherTypeOptions] = useState([]);
  const [facilityTypeMode, setFacilityTypeMode] = useState('open');
  const [facilityTypeIds, setFacilityTypeIds] = useState([]);
  const [facilityTypeOptions, setFacilityTypeOptions] = useState([]);
  const [facilityMode, setFacilityMode] = useState('open');
  const [facilityIds, setFacilityIds] = useState([]);
  const [categoryMode, setCategoryMode] = useState('open');
  const [categoryIds, setCategoryIds] = useState([]);
  const [tariffMode, setTariffMode] = useState('open');
  const [tariffLinks, setTariffLinks] = useState([]);
  const [itemMode, setItemMode] = useState('open');
  const [itemRules, setItemRules] = useState([]);
  const [categoryEvidenceRules, setCategoryEvidenceRules] = useState([]);

  const [tariffScopedCategories, setTariffScopedCategories] = useState([]);
  const [categoriesLookupLoading, setCategoriesLookupLoading] = useState(false);
  const [categoriesLookupHint, setCategoriesLookupHint] = useState(null);
  const [itemsLookupHint, setItemsLookupHint] = useState(null);
  const [planCategories, setPlanCategories] = useState([]);
  const [linkedPlans, setLinkedPlans] = useState([]);
  const [linkedPlansLoading, setLinkedPlansLoading] = useState(false);
  const [facilitySelectOptions, setFacilitySelectOptions] = useState([]);
  const [itemSearchCategoryId, setItemSearchCategoryId] = useState(undefined);

  const [previewFacilityId, setPreviewFacilityId] = useState(undefined);
  const [previewFacilityLabel, setPreviewFacilityLabel] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  const searchFacilities = useCallback(async (search) => {
    const parsed = lib.parseApiEnvelope(await lib.unifiedSearch({
      resource: 'facilities',
      search,
      page: 1,
      pageSize: 50,
    }));
    if (!parsed.ok) return [];
    return lib.unifiedSearchFacilitySelectOptions(parsed.data);
  }, []);

  const searchStandardTariffs = useCallback(async (search) => {
    const parsed = lib.parseApiEnvelope(await lib.getAdminStandardTariffs({ search, limit: 30 }));
    if (!parsed.ok) return [];
    const list = Array.isArray(parsed.data?.tariffs)
      ? parsed.data.tariffs
      : Array.isArray(parsed.data)
        ? parsed.data
        : [];
    return list.map((t) => ({
      value: t.tariff_id ?? t.id,
      label: t.tariff_name ?? t.name ?? `Standard tariff ${t.tariff_id ?? t.id}`,
      tariff_id: t.tariff_id ?? t.id,
    }));
  }, []);

  const resetCatalogSelections = useCallback(() => {
    setCategoryIds([]);
    setItemRules([]);
    setItemSearchCategoryId(undefined);
    setItemsLookupHint(null);
  }, []);

  const tariffLookupParams = useMemo(
    () => buildTariffLookupParams(tariffLinks, isNew ? null : checkinTypeId),
    [tariffLinks, checkinTypeId, isNew],
  );

  const hasTariffLinksForLookup = useMemo(
    () => Boolean(
      tariffLookupParams.checkin_type_id
      || tariffLookupParams.standard_tariff_id
      || tariffLookupParams.tariff_links,
    ),
    [tariffLookupParams],
  );

  const itemsLookupEnabled = hasTariffLinksForLookup || tariffMode === 'open';

  const refreshTariffScopedCategories = useCallback(async () => {
    if (!hasTariffLinksForLookup) {
      setTariffScopedCategories([]);
      setCategoriesLookupHint(null);
      return;
    }
    setCategoriesLookupLoading(true);
    try {
      const parsed = lib.parseApiEnvelope(
        await lib.searchAdminCheckinTypeCategories({ ...tariffLookupParams }),
      );
      if (parsed.ok) {
        const list = Array.isArray(parsed.data) ? parsed.data : [];
        setTariffScopedCategories(categoryOptionsFromLookupList(list));
        setCategoriesLookupHint(parsed.meta?.hint ?? null);
      } else {
        setTariffScopedCategories([]);
        setCategoriesLookupHint(parsed.message || null);
      }
    } catch {
      setTariffScopedCategories([]);
      setCategoriesLookupHint(null);
    } finally {
      setCategoriesLookupLoading(false);
    }
  }, [hasTariffLinksForLookup, tariffLookupParams]);

  const loadLinkedPlansForPool = useCallback(async (poolId) => {
    if (!poolId) {
      setLinkedPlans([]);
      return;
    }
    setLinkedPlansLoading(true);
    try {
      const parsed = lib.parseApiEnvelope(await lib.searchAdminCheckinTypePlansForPool(poolId));
      if (parsed.ok) {
        const list = Array.isArray(parsed.data)
          ? parsed.data
          : Array.isArray(parsed.data?.plans)
            ? parsed.data.plans
            : [];
        setLinkedPlans(list);
      } else {
        setLinkedPlans([]);
      }
    } catch {
      setLinkedPlans([]);
    } finally {
      setLinkedPlansLoading(false);
    }
  }, []);

  const searchItemLookups = useCallback(async (search) => {
    if (!itemsLookupEnabled) {
      setItemsLookupHint('Select a standard price list first (Prices step).');
      return [];
    }
    const params = { search, limit: 20, ...tariffLookupParams };
    if (itemSearchCategoryId != null) params.category_id = itemSearchCategoryId;
    const parsed = lib.parseApiEnvelope(await lib.searchAdminCheckinTypeItems(params));
    if (!parsed.ok) {
      setItemsLookupHint(parsed.message || 'Item search failed');
      return [];
    }
    const list = Array.isArray(parsed.data) ? parsed.data : [];
    setItemsLookupHint(parsed.meta?.hint ?? null);
    return list.map((item, i) => ({
      value: item.standardized_item_id ?? item.item_id ?? item.tariff_item_id ?? item.facility_tariff_item_id ?? `row-${i}`,
      label: item.label || item.item_name || `Item ${item.standardized_item_id ?? item.item_id ?? i}`,
      standardized_item_id: item.standardized_item_id ?? item.item_id ?? null,
      item_id: item.item_id ?? item.standardized_item_id ?? null,
      tariff_item_id: item.tariff_item_id ?? null,
      facility_tariff_item_id: item.facility_tariff_item_id ?? null,
    }));
  }, [itemSearchCategoryId, itemsLookupEnabled, tariffLookupParams]);

  useEffect(() => {
    refreshTariffScopedCategories();
  }, [refreshTariffScopedCategories]);

  useEffect(() => {
    if (tariffMode === 'open') return;
    const policy = categoryPolicyFromTariffLinks(tariffLinks, tariffMode);
    setCategoryMode(policy.categoryMode);
    setCategoryIds(policy.categoryIds);
  }, [tariffLinks, tariffMode]);

  const tariffServiceGroups = useMemo(
    () => tariffServiceGroupSummary(tariffLinks, tariffScopedCategories),
    [tariffLinks, tariffScopedCategories],
  );

  const categoriesDerivedFromPrices = tariffMode !== 'open';

  const loadFacilityTypeOptions = useCallback(async () => {
    try {
      const parsed = lib.parseApiEnvelope(await lib.searchAdminCheckinTypeFacilityTypes());
      if (parsed.ok) {
        const list = Array.isArray(parsed.data) ? parsed.data : [];
        setFacilityTypeOptions(facilityTypeOptionsFromLookupList(list));
      }
    } catch {
      // Non-fatal
    }
  }, []);

  const loadReferenceData = useCallback(async () => {
    try {
      const planCatResp = await lib.getCategories().catch(() => null);
      const planBody = planCatResp?.data;
      const planCats = Array.isArray(planBody?.data) ? planBody.data : [];
      setPlanCategories(
        planCats.map((c) => ({
          value: c.plans_categories_id,
          label: c.plans_categories_name || `Category ${c.plans_categories_id}`,
        })).filter((o) => o.value != null),
      );
    } catch {
      // Non-fatal — pickers may be empty
    }
    try {
      const parsed = lib.parseApiEnvelope(await lib.listAdminCheckinTypes());
      const payload = parsed.ok ? (parsed.data ?? parsed.body) : null;
      const list = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.checkin_types) ? payload.checkin_types : [];
      setOtherTypeOptions(
        list
          .map((row) => row.checkin_type ?? row)
          .filter((t) => Number(t.checkin_type_id) !== Number(checkinTypeId))
          .map((t) => ({ value: t.checkin_type_id, label: t.checkin_type_name })),
      );
    } catch {
      // Non-fatal — picker may be empty
    }
  }, [checkinTypeId]);

  const applyBundle = useCallback((bundle) => {
    const ct = bundle?.checkin_type ?? bundle ?? {};
    const description = ct.checkin_type_description ?? ct.checkin_types_description ?? '';
    const statusRaw = ct.checkin_type_status ?? ct.checkin_types_status ?? ct.status?.value;
    const ent = entitlementFromBundle(bundle);

    generalForm.setFieldsValue({
      checkin_type_name: ct.checkin_type_name ?? '',
      checkin_types_description: description,
      checkin_message: ct.checkin_message ?? '',
      checkin_types_status: Number(statusRaw) === 1,
      display_order: ct.display_order ?? 0,
      parent_checkin_type_id: ct.parent_checkin_type_id ?? null,
      limit_source: ct.parent_checkin_type_id ? 'linked' : 'standalone',
      active_from: ct.active_from ? dayjs(ct.active_from) : null,
      active_to: ct.active_to ? dayjs(ct.active_to) : null,
    });
    entitlementForm.setFieldsValue({
      entitlement_mode: ent.entitlement_mode,
      plans_categories_id: ent.plans_categories_id,
    });
    workflowForm.setFieldsValue(workflowFromBundle(bundle));

    setFacilityTypeMode(bundle?.facility_type_mode ?? 'open');
    setFacilityTypeIds(bundle?.facility_type_ids ?? []);
    setFacilityTypeOptions((prev) => mergeSelectOptions(prev, facilityTypeOptionsFromBundle(bundle)));

    const nextFacilityIds = bundle?.facility_ids ?? [];
    setFacilityMode(bundle?.facility_mode ?? 'open');
    setFacilityIds(nextFacilityIds);
    setFacilitySelectOptions(facilityOptionsFromBundle(bundle));

    setCategoryMode(bundle?.category_mode ?? 'open');
    setCategoryIds(bundle?.category_ids ?? []);
    const hydratedCats = categoryOptionsFromHydrated(bundle?.categories);
    const tariffCats = categoryOptionsFromHydrated(
      (bundle?.tariff_links ?? []).map((l) => l.category).filter(Boolean),
    );
    const mergedCats = mergeSelectOptions(hydratedCats, tariffCats);
    if (mergedCats.length) {
      setTariffScopedCategories((prev) => mergeSelectOptions(prev, mergedCats));
    }

    setLinkedPlans(bundle?.entitlement?.linked_plans ?? []);

    if (ent.limit_pool_category?.plans_categories_id) {
      const poolOpt = {
        value: ent.limit_pool_category.plans_categories_id,
        label: ent.limit_pool_category.plans_categories_name
          || `Category ${ent.limit_pool_category.plans_categories_id}`,
      };
      setPlanCategories((prev) => mergeSelectOptions(prev, [poolOpt]));
    }

    setTariffMode(bundle?.tariff_mode ?? 'open');
    setTariffLinks(
      (bundle?.tariff_links ?? [])
        .map(tariffLinkFromApi)
        .filter((l) => (l.tariff_kind ?? 'standard') === 'standard' && l.standard_tariff_id),
    );

    setItemMode(bundle?.item_mode ?? 'open');
    setItemRules((bundle?.item_rules ?? []).map(itemRuleFromApi));
    setCategoryEvidenceRules((bundle?.category_rules ?? []).map(categoryEvidenceRuleFromApi));
  }, [generalForm, entitlementForm, workflowForm]);

  const loadDetail = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    setError(null);
    try {
      const parsed = lib.parseApiEnvelope(await lib.getAdminCheckinType(checkinTypeId));
      if (!parsed.ok) {
        setError(parsed.message || 'Unable to load encounter type');
        return;
      }
      const bundle = parsed.data ?? parsed.body;
      applyBundle(bundle);
      const ent = entitlementFromBundle(bundle);
      if (ent.entitlement_mode === 'category_required' && ent.plans_categories_id) {
        loadLinkedPlansForPool(ent.plans_categories_id);
      }
    } catch (e) {
      setError(getServerError(e, 'Network error while loading encounter type'));
    } finally {
      setLoading(false);
    }
  }, [applyBundle, checkinTypeId, isNew, loadLinkedPlansForPool]);

  useEffect(() => {
    loadReferenceData();
    loadFacilityTypeOptions();
  }, [loadReferenceData, loadFacilityTypeOptions]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const buildGeneralPayload = () => {
    const g = generalForm.getFieldsValue();
    const e = entitlementForm.getFieldsValue();
    const description = g.checkin_types_description?.trim() || '';
    const status = g.checkin_types_status ? 1 : 0;
    return {
      checkin_type_name: g.checkin_type_name?.trim(),
      checkin_type_description: description,
      checkin_type_status: status,
      checkin_types_description: description,
      checkin_types_status: status,
      display_order: g.display_order ?? 0,
      // limit_source is a UI-only toggle: standalone just means no parent.
      parent_checkin_type_id: g.limit_source === 'linked' ? (g.parent_checkin_type_id ?? null) : null,
      checkin_message: g.checkin_message?.trim() || null,
      active_from: g.active_from ? g.active_from.format('YYYY-MM-DD HH:mm:ss') : null,
      active_to: g.active_to ? g.active_to.format('YYYY-MM-DD HH:mm:ss') : null,
      entitlement_mode: e.entitlement_mode || 'generic',
      plans_categories_id: e.entitlement_mode === 'category_required' ? e.plans_categories_id : null,
      limit_pool_category_id: e.entitlement_mode === 'category_required' ? e.plans_categories_id : null,
    };
  };

  const saveGeneral = async () => {
    await generalForm.validateFields();
    await entitlementForm.validateFields();
    const e = entitlementForm.getFieldsValue();
    if (e.entitlement_mode === 'category_required' && !e.plans_categories_id) {
      notification.error({ message: 'Select a plan benefit category for category-required entitlement' });
      return null;
    }
    setSaving(true);
    try {
      const payload = buildGeneralPayload();
      const resp = isNew
        ? await lib.createAdminCheckinType(payload)
        : await lib.updateAdminCheckinType(checkinTypeId, payload);
      const parsed = lib.parseApiEnvelope(resp);
      if (!lib.notifyApiEnvelope(parsed, {
        successFallback: isNew ? 'Encounter type created' : 'General settings saved',
        errorFallback: 'Save failed',
      })) {
        return null;
      }
      if (isNew) {
        const newId = parsed.data?.checkin_type_id
          ?? parsed.data?.checkin_type?.checkin_type_id
          ?? parsed.data?.id
          ?? parsed.body?.checkin_type_id;
        if (newId) {
          if (onCreated) onCreated(String(newId));
          else navigate(`/plans/encounter-types/${newId}`, { replace: true });
        }
      }
      return parsed.data;
    } catch (e) {
      notification.error({ message: getServerError(e, 'Save failed') });
      return null;
    } finally {
      setSaving(false);
    }
  };

  const savePolicySection = async (request, { successFallback, errorFallback }) => {
    setSaving(true);
    try {
      const parsed = lib.parseApiEnvelope(await request());
      lib.notifyApiEnvelope(parsed, { successFallback, errorFallback });
    } catch (e) {
      notification.error({ message: getServerError(e, errorFallback) });
    } finally {
      setSaving(false);
    }
  };

  const saveFacilityTypes = async () => {
    if (isNew) {
      notification.warning({ message: 'Save general settings first to create this type' });
      return;
    }
    await savePolicySection(
      () => lib.replaceAdminCheckinTypeFacilityTypes(checkinTypeId, {
        mode: facilityTypeMode,
        facility_type_ids: facilityTypeIds,
      }),
      { successFallback: 'Facility kinds saved', errorFallback: 'Failed to save facility kinds' },
    );
  };

  const saveFacilities = async () => {
    if (isNew) {
      notification.warning({ message: 'Save general settings first to create this type' });
      return;
    }
    await savePolicySection(
      () => lib.replaceAdminCheckinTypeFacilities(checkinTypeId, {
        mode: facilityMode,
        facility_ids: facilityIds,
      }),
      { successFallback: 'Specific sites saved', errorFallback: 'Failed to save specific sites' },
    );
  };

  const saveCategories = async () => {
    if (isNew) {
      notification.warning({ message: 'Save general settings first to create this type' });
      return;
    }
    await savePolicySection(
      () => lib.replaceAdminCheckinTypeCategories(checkinTypeId, {
        mode: categoryMode,
        category_ids: categoryIds,
      }),
      { successFallback: 'Category policy saved', errorFallback: 'Failed to save category policy' },
    );
  };

  const saveTariffs = async () => {
    if (isNew) {
      notification.warning({ message: 'Save general settings first to create this type' });
      return;
    }
    const links = tariffLinks
      .filter((l) => (l.tariff_kind ?? 'standard') === 'standard' && l.standard_tariff_id)
      .map(({
      key,
      standard_tariff_label,
      facility_tariff_label,
      link_facility_id,
      link_facility_label,
      ...rest
    }) => ({
      ...rest,
      tariff_kind: 'standard',
    }));
    const policy = categoryPolicyFromTariffLinks(tariffLinks, tariffMode);
    setCategoryMode(policy.categoryMode);
    setCategoryIds(policy.categoryIds);

    setSaving(true);
    try {
      const tariffParsed = lib.parseApiEnvelope(
        await lib.replaceAdminCheckinTypeTariffs(checkinTypeId, {
          mode: tariffMode,
          tariff_links: links,
        }),
      );
      if (!tariffParsed.ok) {
        lib.notifyApiEnvelope(tariffParsed, {
          successFallback: 'Tariff policy saved',
          errorFallback: 'Failed to save tariff policy',
        });
        return;
      }
      if (tariffMode !== 'open') {
        const catParsed = lib.parseApiEnvelope(
          await lib.replaceAdminCheckinTypeCategories(checkinTypeId, {
            mode: policy.categoryMode,
            category_ids: policy.categoryIds,
          }),
        );
        lib.notifyApiEnvelope(catParsed, {
          successFallback: 'Prices and service groups saved',
          errorFallback: 'Prices saved but service groups failed to sync',
        });
        return;
      }
      lib.notifyApiEnvelope(tariffParsed, {
        successFallback: 'Tariff policy saved',
        errorFallback: 'Failed to save tariff policy',
      });
    } catch (e) {
      notification.error({ message: getServerError(e, 'Failed to save tariff policy') });
    } finally {
      setSaving(false);
    }
  };

  const saveItems = async () => {
    if (isNew) {
      notification.warning({ message: 'Save general settings first to create this type' });
      return;
    }
    const rules = itemRules.map(({ key, label, ...rest }) => {
      const row = { ...rest };
      if (row.standardized_item_id == null) delete row.standardized_item_id;
      if (row.item_id == null) delete row.item_id;
      if (row.tariff_item_id == null) delete row.tariff_item_id;
      if (row.facility_tariff_item_id == null) delete row.facility_tariff_item_id;
      if (!row.evidence_mode) row.evidence_mode = 'not_needed';
      return row;
    });
    await savePolicySection(
      () => lib.replaceAdminCheckinTypeItems(checkinTypeId, {
        mode: itemMode,
        item_rules: rules,
      }),
      { successFallback: 'Item rules saved', errorFallback: 'Failed to save item rules' },
    );
  };

  const saveWorkflow = async () => {
    if (isNew) {
      notification.warning({ message: 'Save general settings first to create this type' });
      return;
    }
    try {
      await workflowForm.validateFields();
    } catch {
      return;
    }
    const wf = workflowForm.getFieldsValue();
    const categoryRules = categoryEvidenceRules
      .filter((r) => r.category_id != null)
      .map(({ key, category_name, ...rest }) => rest);
    setSaving(true);
    try {
      const parsed = lib.parseApiEnvelope(await lib.updateAdminCheckinType(checkinTypeId, {
        workflow: {
          pa_header_evidence_mode: wf.pa_header_evidence_mode || 'not_needed',
        },
        category_rules: categoryRules,
      }));
      lib.notifyApiEnvelope(parsed, {
        successFallback: 'Workflow settings saved',
        errorFallback: 'Failed to save workflow settings',
      });
    } catch (e) {
      notification.error({ message: getServerError(e, 'Failed to save workflow settings') });
    } finally {
      setSaving(false);
    }
  };

  const runPreview = async () => {
    if (isNew || !previewFacilityId) {
      notification.warning({ message: 'Select a facility to preview' });
      return;
    }
    setPreviewLoading(true);
    setPreviewData(null);
    try {
      const parsed = lib.parseApiEnvelope(
        await lib.previewAdminCheckinType(checkinTypeId, previewFacilityId),
      );
      if (!parsed.ok) {
        notification.error({ message: parsed.message || 'Preview failed' });
        return;
      }
      setPreviewData(parsed.data ?? parsed.body);
    } catch (e) {
      notification.error({ message: getServerError(e, 'Preview failed') });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleFacilityIdsChange = (values) => {
    setFacilityIds(values);
  };

  const tariffColumns = useMemo(() => [
    {
      title: 'Standard tariff',
      dataIndex: 'standard_tariff_id',
      render: (v, row) => (
        <CheckinTypeTypeahead
          allowClear
          minLength={1}
          style={{ width: '100%' }}
          placeholder="Search standard tariffs…"
          value={v}
          fetchOptions={searchStandardTariffs}
          options={v ? [{ value: v, label: row.standard_tariff_label || `Standard tariff #${v}` }] : []}
          onChange={(val, option) => {
            setTariffLinks((prev) => prev.map((r) => (r.key === row.key ? {
              ...r,
              tariff_kind: 'standard',
              standard_tariff_id: val ?? null,
              standard_tariff_label: option?.label ?? null,
              category_id: null,
            } : r)));
            resetCatalogSelections();
          }}
        />
      ),
    },
    {
      title: 'Service group',
      dataIndex: 'category_id',
      width: 200,
      render: (v, row) => (
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          style={{ width: '100%' }}
          placeholder={tariffMode !== 'open' ? 'Select service group…' : 'All groups on tariff'}
          value={v}
          options={tariffScopedCategories}
          onChange={(val) => {
            setTariffLinks((prev) => prev.map((r) => (r.key === row.key ? { ...r, category_id: val ?? null } : r)));
          }}
        />
      ),
    },
    {
      title: '',
      width: 48,
      render: (_, row) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => {
            setTariffLinks((prev) => prev.filter((r) => r.key !== row.key));
            resetCatalogSelections();
          }}
        />
      ),
    },
  ], [resetCatalogSelections, tariffMode, tariffScopedCategories, searchStandardTariffs]);

  const categoryEvidenceColumns = useMemo(() => [
    {
      title: 'Category',
      dataIndex: 'category_id',
      render: (v, row) => (
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          style={{ width: '100%' }}
          placeholder="Select category"
          value={v}
          options={tariffScopedCategories}
          onChange={(val, option) => {
            setCategoryEvidenceRules((prev) => prev.map((r) => (r.key === row.key ? {
              ...r,
              category_id: val ?? null,
              category_name: option?.label ?? null,
            } : r)));
          }}
        />
      ),
    },
    {
      title: 'Document rule',
      dataIndex: 'evidence_mode',
      width: 180,
      render: (v, row) => (
        <Select
          style={{ width: '100%' }}
          value={v || 'not_needed'}
          options={EVIDENCE_MODES}
          onChange={(val) => {
            setCategoryEvidenceRules((prev) => prev.map((r) => (r.key === row.key ? { ...r, evidence_mode: val } : r)));
          }}
        />
      ),
    },
    {
      title: '',
      width: 48,
      render: (_, row) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => setCategoryEvidenceRules((prev) => prev.filter((r) => r.key !== row.key))}
        />
      ),
    },
  ], [tariffScopedCategories]);

  const itemRuleColumns = useMemo(() => [
    {
      title: 'Tariff line / procedure',
      dataIndex: 'label',
      render: (v, row) => (
        <CheckinTypeTypeahead
          allowClear
          minLength={2}
          style={{ width: '100%' }}
          placeholder={itemsLookupEnabled ? 'Search items (min 2 chars)…' : 'Set up Prices first'}
          disabled={!itemsLookupEnabled}
          value={itemRuleValue(row)}
          fetchOptions={searchItemLookups}
          options={itemRuleValue(row) ? [{ value: itemRuleValue(row), label: v || row.label || `Item #${itemRuleValue(row)}` }] : []}
          onChange={(val, option) => {
            if (!val) {
              setItemRules((prev) => prev.map((r) => (r.key === row.key ? { ...emptyItemRule(), key: r.key } : r)));
              return;
            }
            setItemRules((prev) => prev.map((r) => (r.key === row.key ? {
              ...r,
              label: option?.label ?? r.label,
              standardized_item_id: option?.standardized_item_id ?? null,
              item_id: option?.item_id ?? null,
              tariff_item_id: option?.tariff_item_id ?? null,
              facility_tariff_item_id: option?.facility_tariff_item_id ?? null,
            } : r)));
          }}
        />
      ),
    },
    {
      title: 'Per-line document',
      dataIndex: 'evidence_mode',
      width: 180,
      render: (v, row) => (
        <Select
          style={{ width: '100%' }}
          value={v || 'not_needed'}
          options={EVIDENCE_MODES}
          onChange={(val) => {
            setItemRules((prev) => prev.map((r) => (r.key === row.key ? { ...r, evidence_mode: val } : r)));
          }}
        />
      ),
    },
    {
      title: '',
      width: 48,
      render: (_, row) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => setItemRules((prev) => prev.filter((r) => r.key !== row.key))}
        />
      ),
    },
  ], [itemsLookupEnabled, searchItemLookups]);

  const previewCategories = previewData?.categories ?? previewData?.category_counts ?? [];
  const previewRows = Array.isArray(previewCategories) ? previewCategories : [];

  const facilityTypeLabels = useMemo(
    () => facilityTypeIds.map((id) => facilityTypeOptions.find((o) => o.value === id)?.label || `Kind #${id}`),
    [facilityTypeIds, facilityTypeOptions],
  );
  const facilityLabels = useMemo(
    () => facilityIds.map((id) => facilitySelectOptions.find((o) => o.value === id)?.label || `Facility #${id}`),
    [facilityIds, facilitySelectOptions],
  );
  const categoryLabels = useMemo(
    () => categoryIds.map((id) => tariffScopedCategories.find((o) => o.value === id)?.label || `Category #${id}`),
    [categoryIds, tariffScopedCategories],
  );
  const entitlementMode = Form.useWatch('entitlement_mode', entitlementForm);
  const poolId = Form.useWatch('plans_categories_id', entitlementForm);
  const poolLabel = planCategories.find((p) => p.value === poolId)?.label;

  const tabLabel = (key) => WIZARD_STEPS.find((s) => s.key === key)?.title || key;

  return (
    <div style={{ padding: '16px 24px' }}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Space wrap>
          <Button icon={<ArrowLeftOutlined />} onClick={() => (onBack ? onBack() : navigate('/plans?tab=encounter-types'))}>
            {onBack ? 'Back to encounter types' : 'Back to Plans'}
          </Button>
          <Title level={3} style={{ margin: 0 }}>
            {isNew ? 'New encounter type' : `Encounter type #${checkinTypeId}`}
          </Title>
        </Space>

        {error && <Alert type="error" message={error} showIcon />}

        <SetupWizardNav activeKey={activeTab} onChange={setActiveTab} isNew={isNew} />

        <Spin spinning={loading}>
          <Tabs activeKey={activeTab} onChange={setActiveTab} renderTabBar={() => null} items={[
            {
              key: 'general',
              label: tabLabel('general'),
              children: (
                <Card>
                  <TabGoal tabKey="general" />
                  <Form form={generalForm} layout="vertical" initialValues={{ checkin_types_status: true, display_order: 0, limit_source: 'standalone' }}>
                    <Row gutter={16}>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="checkin_type_name"
                          label="Encounter type name"
                          tooltip="Short identifier shown at check-in and on PA (e.g. dental, optical)."
                          rules={[{ required: true, message: 'Name is required' }]}
                          extra="Use a clear, lowercase-style name staff will recognise at the facility."
                        >
                          <Input placeholder="e.g. dental, optical, maternity" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="display_order"
                          label="Sort order"
                          tooltip="Lower numbers appear earlier in encounter-type pickers."
                          extra="Use 1 for first in the list. Types left at 0 sort last."
                        >
                          <InputNumber style={{ width: '100%' }} min={0} placeholder="0" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="limit_source"
                          label="Limit source"
                          tooltip="Limits are set per plan, in Plan Design. This decides whether that plan-level limit is this type's own, or inherited from another type."
                        >
                          <Select
                            options={[
                              { value: 'standalone', label: 'Standalone — has its own limit' },
                              { value: 'linked', label: 'Linked — draws its limit from another type' },
                            ]}
                            onChange={(v) => {
                              if (v === 'standalone') generalForm.setFieldsValue({ parent_checkin_type_id: null });
                            }}
                          />
                        </Form.Item>
                      </Col>
                      <Form.Item
                        noStyle
                        shouldUpdate={(prev, cur) => prev.limit_source !== cur.limit_source}
                      >
                        {() => (generalForm.getFieldValue('limit_source') === 'linked' ? (
                          <Col xs={24} md={12}>
                            <Form.Item
                              name="parent_checkin_type_id"
                              label="Draws its limit from"
                              rules={[{ required: true, message: 'Pick the encounter type this one draws its limit from' }]}
                              extra="Plan Design stops asking for a limit for this type — it inherits this one's."
                            >
                              <Select
                                showSearch
                                optionFilterProp="label"
                                placeholder="e.g. Outpatient"
                                options={otherTypeOptions}
                              />
                            </Form.Item>
                          </Col>
                        ) : null)}
                      </Form.Item>
                      <Col span={24}>
                        <Form.Item
                          name="checkin_types_description"
                          label="Description (optional)"
                          extra="Shown to admins only — explain when staff should choose this type."
                        >
                          <Input.TextArea rows={2} placeholder="e.g. Routine and emergency dental visits" />
                        </Form.Item>
                      </Col>
                      <Col span={24}>
                        <Form.Item
                          name="checkin_message"
                          label="Check-in notification message (optional)"
                          tooltip="Custom push/SMS copy when a member is checked in under this type."
                          extra={(
                            <>
                              Max 500 characters. Leave empty for the default message.
                              {' '}Placeholders: {CHECKIN_MESSAGE_PLACEHOLDERS.join(', ')}.
                            </>
                          )}
                          rules={[{ max: 500, message: 'Maximum 500 characters' }]}
                        >
                          <Input.TextArea
                            rows={3}
                            maxLength={500}
                            showCount
                            placeholder='e.g. {enrollee_first_name} has been checked in for {checkin_type} care at {facility_name}.'
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={8}>
                        <Form.Item
                          name="checkin_types_status"
                          label="Enabled for check-in"
                          valuePropName="checked"
                          tooltip="Disabled types are hidden from facility pickers and reject new check-ins."
                          extra="Category-required types need a limit pool before you can enable them."
                        >
                          <Switch checkedChildren="On" unCheckedChildren="Off" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={8}>
                        <Form.Item
                          name="active_from"
                          label="Available from (optional)"
                          tooltip="Leave empty for no start date. Type is hidden before this moment."
                        >
                          <DatePicker showTime style={{ width: '100%' }} placeholder="No start limit" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={8}>
                        <Form.Item
                          name="active_to"
                          label="Available until (optional)"
                          tooltip="Leave empty for no end date. Type is hidden after this moment."
                        >
                          <DatePicker showTime style={{ width: '100%' }} placeholder="No end limit" />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Form>
                  <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={saveGeneral}>
                    {isNew ? 'Create & continue' : 'Save basics'}
                  </Button>
                  <NextStepHint nextTabKey="entitlement" />
                </Card>
              ),
            },
            {
              key: 'entitlement',
              label: tabLabel('entitlement'),
              children: (
                <Card>
                  <TabGoal tabKey="entitlement" />
                  <LiveSummary type={entitlementMode === 'category_required' && !poolId ? 'warning' : 'success'}>
                    {entitlementMode === 'category_required'
                      ? (poolId
                        ? `Only members whose plan includes “${poolLabel || 'selected benefit'}”. Set caps under Plans → Limits.`
                        : 'Pick a plan benefit below — members without it cannot check in.')
                      : 'Any active member can use this visit type.'}
                  </LiveSummary>
                  <Form
                    form={entitlementForm}
                    layout="vertical"
                    initialValues={{ entitlement_mode: 'generic' }}
                    onValuesChange={(changed) => {
                      if ('plans_categories_id' in changed || 'entitlement_mode' in changed) {
                        const mode = entitlementForm.getFieldValue('entitlement_mode');
                        const poolId = entitlementForm.getFieldValue('plans_categories_id');
                        if (mode === 'category_required' && poolId) {
                          loadLinkedPlansForPool(poolId);
                        } else {
                          setLinkedPlans([]);
                        }
                      }
                    }}
                  >
                    <Form.Item name="entitlement_mode" label="Who can check in?">
                      <EntitlementModePicker />
                    </Form.Item>
                    <Form.Item noStyle shouldUpdate={(prev, cur) => prev.entitlement_mode !== cur.entitlement_mode}>
                      {({ getFieldValue }) => getFieldValue('entitlement_mode') === 'category_required' ? (
                        <Form.Item
                          name="plans_categories_id"
                          label="Which plan benefit is required?"
                          rules={[{ required: true, message: 'Choose a benefit — e.g. Dental, Optical' }]}
                          extra="This is the same benefit you add on each plan under Plans → Limits."
                        >
                          <Select
                            showSearch
                            optionFilterProp="label"
                            placeholder="e.g. Dental, Maternity, Optical…"
                            options={planCategories}
                          />
                        </Form.Item>
                      ) : null}
                    </Form.Item>
                    <Form.Item noStyle shouldUpdate={(prev, cur) => prev.entitlement_mode !== cur.entitlement_mode}>
                      {({ getFieldValue }) => (getFieldValue('entitlement_mode') === 'category_required' ? (
                        <div style={{ marginBottom: 16 }}>
                          <Text strong>Plans that already include this benefit</Text>
                          <FieldHint>To change limits, edit each plan under Plans → Limits.</FieldHint>
                          <LinkedPlansPanel
                            plans={linkedPlans}
                            loading={linkedPlansLoading}
                            poolName={
                              planCategories.find(
                                (p) => p.value === getFieldValue('plans_categories_id'),
                              )?.label
                            }
                          />
                        </div>
                      ) : null)}
                    </Form.Item>
                  </Form>
                  <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={saveGeneral}>
                    Save who can use
                  </Button>
                  <NextStepHint nextTabKey="facility-types" />
                </Card>
              ),
            },
            {
              key: 'facility-types',
              label: tabLabel('facility-types'),
              children: (
                <Card>
                  <TabGoal tabKey="facility-types" />
                  <LiveSummary>
                    {describePolicyMode(facilityTypeMode, facilityTypeIds, facilityTypeLabels, 'site kinds')}
                  </LiveSummary>
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Examples"
                    description={(
                      <ul style={{ margin: '4px 0 0', paddingLeft: 20, fontSize: 13 }}>
                        <li><strong>Any dental clinic</strong> — Only these → Dental</li>
                        <li><strong>Two named dental sites</strong> — Kinds → Dental, then Sites → pick those clinics</li>
                        <li><strong>Everywhere except hospitals</strong> — All except these → Hospital</li>
                      </ul>
                    )}
                  />
                  <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    <div>
                      <Text strong>Which kinds of facility can offer this?</Text>
                      <div style={{ marginTop: 12 }}>
                        <PolicyModePicker
                          value={facilityTypeMode}
                          onChange={setFacilityTypeMode}
                          noun="site kinds"
                        />
                      </div>
                    </div>
                    {facilityTypeMode !== 'open' && (
                    <div>
                      <Text strong>Pick site kinds</Text>
                      <FieldHint>e.g. Dental, Hospital, Pharmacy, Laboratory</FieldHint>
                      <Select
                        mode="multiple"
                        showSearch
                        optionFilterProp="label"
                        style={{ width: '100%', marginTop: 8 }}
                        placeholder="Choose facility kinds…"
                        value={facilityTypeIds}
                        options={facilityTypeOptions}
                        onChange={setFacilityTypeIds}
                      />
                    </div>
                    )}
                    <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={saveFacilityTypes} disabled={isNew}>
                      Save kinds
                    </Button>
                    <NextStepHint nextTabKey="facilities" />
                  </Space>
                </Card>
              ),
            },
            {
              key: 'facilities',
              label: tabLabel('facilities'),
              children: (
                <Card>
                  <TabGoal tabKey="facilities" />
                  <LiveSummary>
                    {facilityTypeMode !== 'open'
                      ? `${describePolicyMode(facilityTypeMode, facilityTypeIds, facilityTypeLabels, 'site kinds')} Then: ${describePolicyMode(facilityMode, facilityIds, facilityLabels, 'named sites')}`
                      : describePolicyMode(facilityMode, facilityIds, facilityLabels, 'named sites')}
                  </LiveSummary>
                  <FieldHint style={{ marginBottom: 12 }}>
                    Both rules apply together — a site must pass the <strong>Kinds</strong> step and this step.
                  </FieldHint>
                  <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    <div>
                      <Text strong>Any extra filter on named sites?</Text>
                      <div style={{ marginTop: 12 }}>
                        <PolicyModePicker value={facilityMode} onChange={setFacilityMode} noun="facilities" />
                      </div>
                    </div>
                    {facilityMode !== 'open' && (
                    <div>
                      <Text strong>Pick specific sites</Text>
                      <FieldHint>Search by hospital or clinic name — use when you need named locations, not just a kind.</FieldHint>
                    </div>
                    )}
                    {facilityMode !== 'open' && (
                    <CheckinTypeTypeahead
                      mode="multiple"
                      minLength={3}
                      style={{ width: '100%' }}
                      placeholder="Start typing a facility name…"
                      value={facilityIds}
                      options={facilitySelectOptions}
                      fetchOptions={searchFacilities}
                      onChange={(values) => setFacilityIds(values ?? [])}
                      onSelect={(_, option) => {
                        setFacilitySelectOptions((prev) => (
                          prev.some((o) => o.value === option.value) ? prev : [...prev, option]
                        ));
                      }}
                    />
                    )}
                    <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={saveFacilities} disabled={isNew}>
                      Save sites
                    </Button>
                    <NextStepHint nextTabKey="tariffs" />
                  </Space>
                </Card>
              ),
            },
            {
              key: 'tariffs',
              label: tabLabel('tariffs'),
              children: (
                <Card>
                  <TabGoal tabKey="tariffs" />
                  <LiveSummary>
                    {tariffMode === 'open'
                      ? 'Each facility uses its own active price list — no links needed.'
                      : describePolicyMode(tariffMode, tariffLinks, tariffLinks.map((l) => l.standard_tariff_label || 'tariff'), 'price lists')}
                  </LiveSummary>
                  <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    <div>
                      <Text strong>How are price lists chosen?</Text>
                      <div style={{ marginTop: 12 }}>
                        <PolicyModePicker value={tariffMode} onChange={setTariffMode} noun="price lists" />
                      </div>
                    </div>
                    {tariffMode !== 'open' && (
                    <FieldHint>
                      Link one or more <strong>standard (HMO) price lists</strong>.
                      Pick the <strong>service group</strong> for each row (e.g. Lab, Drugs) — that is what staff see when filing PA.
                      Only active standard tariffs can be linked.
                    </FieldHint>
                    )}
                    {tariffMode !== 'open' && (
                    <Table
                      size="small"
                      pagination={false}
                      rowKey="key"
                      columns={tariffColumns}
                      dataSource={tariffLinks}
                      locale={{ emptyText: 'Add a price list below' }}
                    />
                    )}
                    {tariffMode !== 'open' && (
                    <Button icon={<PlusOutlined />} onClick={() => setTariffLinks((p) => [...p, emptyTariffLink()])}>
                      Add price list
                    </Button>
                    )}
                    <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={saveTariffs} disabled={isNew}>
                      Save prices
                    </Button>
                    <NextStepHint nextTabKey={categoriesDerivedFromPrices ? 'items' : 'categories'} />
                  </Space>
                </Card>
              ),
            },
            {
              key: 'categories',
              label: tabLabel('categories'),
              children: (
                <Card>
                  <TabGoal tabKey="categories" />
                  <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    {categoriesDerivedFromPrices ? (
                      <>
                        <Alert
                          type="info"
                          showIcon
                          message="Service groups come from Prices"
                          description="Each price list row defines the service group for PA (e.g. Aman 2020 → Lab). Save Prices to apply. You do not need to pick groups again here."
                        />
                        {!hasTariffLinksForLookup && (
                          <Alert
                            type="warning"
                            showIcon
                            message="Add price lists first"
                            description="Open the Prices step, add standard tariffs with service groups, and click Save prices."
                          />
                        )}
                        {tariffServiceGroups.length > 0 && (
                          <div>
                            <Text strong>Configured from price lists</Text>
                            <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
                              {tariffServiceGroups.map((line) => (
                                <li key={line}><Text>{line}</Text></li>
                              ))}
                            </ul>
                          </div>
                        )}
                        <LiveSummary>
                          {categoryMode === 'open'
                            ? 'All service groups on the linked price lists are allowed at PA.'
                            : describePolicyMode(categoryMode, categoryIds, categoryLabels, 'service groups')}
                        </LiveSummary>
                        <NextStepHint nextTabKey="items" />
                      </>
                    ) : (
                      <>
                    {!hasTariffLinksForLookup && tariffMode !== 'open' && (
                      <Alert
                        type="warning"
                        showIcon
                        message="Add price lists first"
                        description="Open the Prices step, add at least one standard tariff, and click Save prices."
                      />
                    )}
                    {categoriesLookupHint && (
                      <Alert type="info" showIcon message={categoriesLookupHint} />
                    )}
                    <LiveSummary>
                      {describePolicyMode(categoryMode, categoryIds, categoryLabels, 'service groups')}
                    </LiveSummary>
                    <div>
                      <Text strong>Which service groups can be filed on PA?</Text>
                      <div style={{ marginTop: 12 }}>
                        <PolicyModePicker value={categoryMode} onChange={setCategoryMode} noun="service groups" />
                      </div>
                    </div>
                    {categoryMode !== 'open' && (
                    <div>
                      <Text strong>Select service groups</Text>
                      <FieldHint>Examples: Drugs, Dental, Laboratory.</FieldHint>
                    </div>
                    )}
                    {categoryMode !== 'open' && (
                    <Select
                      mode="multiple"
                      showSearch
                      optionFilterProp="label"
                      style={{ width: '100%' }}
                      placeholder={hasTariffLinksForLookup || tariffMode === 'open' ? 'Choose service groups…' : 'Set up Prices first'}
                      value={categoryIds}
                      options={tariffScopedCategories}
                      onChange={setCategoryIds}
                      disabled={!hasTariffLinksForLookup && tariffMode !== 'open'}
                      loading={categoriesLookupLoading}
                    />
                    )}
                    {categoryMode !== 'open' && (
                    <Button onClick={refreshTariffScopedCategories} disabled={!hasTariffLinksForLookup && tariffMode !== 'open'}>
                      Reload list from price lists
                    </Button>
                    )}
                    <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={saveCategories} disabled={isNew}>
                      Save services
                    </Button>
                    <NextStepHint nextTabKey="items" />
                      </>
                    )}
                  </Space>
                </Card>
              ),
            },
            {
              key: 'items',
              label: tabLabel('items'),
              children: (
                <Card>
                  <TabGoal tabKey="items" />
                  <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    {!itemsLookupEnabled && tariffMode !== 'open' && (
                      <Alert
                        type="warning"
                        showIcon
                        message="Add price lists first"
                        description="Open the Prices step, add at least one standard tariff, then return here to search procedures."
                      />
                    )}
                    {itemsLookupHint && (
                      <Alert type="info" showIcon message={itemsLookupHint} />
                    )}
                    <LiveSummary>
                      {itemMode === 'open'
                        ? 'All procedures on the price list are allowed — no extra rules.'
                        : describePolicyMode(itemMode, itemRules, itemRules.map((r) => r.label || 'procedure'), 'procedures')}
                    </LiveSummary>
                    <div>
                      <Text strong>Need to limit specific procedures?</Text>
                      <div style={{ marginTop: 12 }}>
                        <PolicyModePicker value={itemMode} onChange={setItemMode} noun="procedures" />
                      </div>
                    </div>
                    {itemMode !== 'open' && (
                    <div>
                      <Text strong>Filter search (optional)</Text>
                      <FieldHint>Narrow search to one service group if the list is long.</FieldHint>
                      <Select
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        style={{ width: '100%', marginTop: 4 }}
                        placeholder="All categories on tariff"
                        value={itemSearchCategoryId}
                        options={tariffScopedCategories}
                        onChange={(val) => {
                          setItemSearchCategoryId(val);
                          setItemsLookupHint(null);
                        }}
                        disabled={!hasTariffLinksForLookup && tariffMode !== 'open'}
                      />
                    </div>
                    )}
                    {itemMode !== 'open' && (
                    <>
                    <FieldHint>{EVIDENCE_LINE_HINT}</FieldHint>
                    <FieldHint>Search by procedure or drug name (min 2 characters).</FieldHint>
                    <Table
                      size="small"
                      pagination={false}
                      rowKey="key"
                      columns={itemRuleColumns}
                      dataSource={itemRules}
                      locale={{ emptyText: 'Add procedures below — or leave empty to allow all on the price list' }}
                    />
                    <Button icon={<PlusOutlined />} onClick={() => setItemRules((p) => [...p, emptyItemRule()])} disabled={!itemsLookupEnabled}>
                      Add procedure
                    </Button>
                    </>
                    )}
                    <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={saveItems} disabled={isNew}>
                      Save procedures
                    </Button>
                    <NextStepHint nextTabKey="workflow" />
                  </Space>
                </Card>
              ),
            },
            {
              key: 'workflow',
              label: tabLabel('workflow'),
              children: (
                <Card>
                  <TabGoal tabKey="workflow" />
                  <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    <Alert
                      type="info"
                      showIcon
                      message={EVIDENCE_BUNDLE_PRESET.title}
                      description={EVIDENCE_BUNDLE_PRESET.description}
                    />
                    <LiveSummary>
                      Facilities file PA → upload any required files → click Finalize. Use header documents for one encounter file; use category or item rules only when each line needs its own proof.
                    </LiveSummary>
                    <Form
                      form={workflowForm}
                      layout="vertical"
                      initialValues={{ pa_header_evidence_mode: 'not_needed' }}
                    >
                      <Form.Item
                        name="pa_header_evidence_mode"
                        label={EVIDENCE_HEADER_LABEL}
                        extra={(
                          <Form.Item noStyle shouldUpdate>
                            {({ getFieldValue }) => (
                              <>
                                <FieldHint>{EVIDENCE_HEADER_HINT}</FieldHint>
                                <FieldHint>{EVIDENCE_MODE_HELP[getFieldValue('pa_header_evidence_mode')]}</FieldHint>
                              </>
                            )}
                          </Form.Item>
                        )}
                      >
                        <EvidenceModePicker />
                      </Form.Item>
                    </Form>
                    <div>
                      <Text strong>{EVIDENCE_LINE_LABEL}</Text>
                      <FieldHint>{EVIDENCE_LINE_HINT}</FieldHint>
                      <Table
                        size="small"
                        pagination={false}
                        rowKey="key"
                        columns={categoryEvidenceColumns}
                        dataSource={categoryEvidenceRules}
                        locale={{ emptyText: 'No category evidence rules' }}
                      />
                      <Button
                        icon={<PlusOutlined />}
                        style={{ marginTop: 8 }}
                        onClick={() => setCategoryEvidenceRules((p) => [...p, emptyCategoryEvidenceRule()])}
                      >
                        Add category rule
                      </Button>
                    </div>
                    <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={saveWorkflow} disabled={isNew}>
                      Save documents &amp; claims
                    </Button>
                    <NextStepHint nextTabKey="preview" />
                  </Space>
                </Card>
              ),
            },
            {
              key: 'preview',
              label: tabLabel('preview'),
              children: (
                <Card>
                  <TabGoal tabKey="preview" />
                  <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    <FieldHint>
                      Choose a hospital or clinic to see how many service groups and procedures members could file.
                    </FieldHint>
                    <Space wrap>
                      <CheckinTypeTypeahead
                        allowClear
                        minLength={3}
                        style={{ minWidth: 320 }}
                        placeholder="Search facility for preview…"
                        value={previewFacilityId}
                        options={previewFacilityId ? [{ value: previewFacilityId, label: previewFacilityLabel || `Facility #${previewFacilityId}` }] : []}
                        fetchOptions={searchFacilities}
                        onChange={(val, option) => {
                          setPreviewFacilityId(val ?? undefined);
                          setPreviewFacilityLabel(option?.label ?? null);
                        }}
                      />
                      <Button type="primary" loading={previewLoading} onClick={runPreview} disabled={isNew}>
                        Run preview
                      </Button>
                    </Space>
                    {previewData && (
                      <>
                        <Descriptions bordered size="small" column={2}>
                          <Descriptions.Item label="Categories">{previewData?.category_count ?? previewRows.length ?? '—'}</Descriptions.Item>
                          <Descriptions.Item label="Items">{previewData?.item_count ?? '—'}</Descriptions.Item>
                        </Descriptions>
                        {previewRows.length > 0 && (
                          <Table
                            size="small"
                            pagination={false}
                            rowKey={(r, i) => r?.category_id ?? i}
                            dataSource={previewRows}
                            columns={[
                              { title: 'Category', dataIndex: 'category_name', render: (v, r) => v ?? r?.name ?? r?.category_id },
                              { title: 'Items', dataIndex: 'item_count', render: (v) => v ?? '—' },
                            ]}
                          />
                        )}
                      </>
                    )}
                  </Space>
                </Card>
              ),
            },
          ]} />
        </Spin>
      </Space>
    </div>
  );
};

export default AdminCheckinTypeDetail;
